import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "~/react/ui/button"
import { Checkbox } from "~/react/ui/checkbox"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import { Label } from "~/react/ui/label"
import { Hint } from "~/react/ui/hint"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "~/react/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "~/react/ui/dropdown-menu"
import { NumberPicker } from "~/react/ui/number-picker"
import SignatureEditor from "~/react/components/signature_editor"
import ManualAccountsSection from "~/react/components/email_accounts/manual-accounts-section"
import type {
  ManualAccount,
  ManualSender,
  PreviewRow,
  Suggestion,
  TeamMember,
} from "~/react/components/email_accounts/types"
import { EmailAccountCard } from "~/react/components/email_accounts/email-account-card"

// Declare i18n as a global
declare const i18n: {
  t: (key: string, options?: Record<string, any>) => string
}

type Props = {
  teamMembers: TeamMember[]
  manualMode: boolean
  setManualMode: (v: boolean) => void
  selectedSenders: Record<string, boolean>
  setSelectedSenders: (
    fn: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void
  manualSenders: ManualSender[]
  setManualSenders: (fn: (prev: ManualSender[]) => ManualSender[]) => void
  manualFirst: string
  setManualFirst: (v: string) => void
  manualLast: string
  setManualLast: (v: string) => void
  manualSenderError?: string
  setManualSenderError: (err?: string) => void
  showManualSenderForm: boolean
  setShowManualSenderForm: (v: boolean) => void
  selectedDomains: string[]
  suggestions: Suggestion[]
  accountGenerationMap: Record<string, boolean>
  setAccountGenerationMap: (
    fn: (prev: Record<string, boolean>) => Record<string, boolean>,
  ) => void
  emailAccountsPerSender: number
  setEmailAccountsPerSender: (n: number) => void
  signature: string
  setSignature: (s: string) => void
  canPreview: boolean
  buildPreview: () => void
  manualAccounts: ManualAccount[]
  setManualAccounts: (fn: (prev: ManualAccount[]) => ManualAccount[]) => void
  duplicateEmailMap: Record<string, number>
  preview: PreviewRow[]
  setPreview: (fn: (prev: PreviewRow[]) => PreviewRow[]) => void
  showPreview: boolean
  setShowPreview: (v: boolean) => void
  currencyCode?: string
  googlePriceCents?: number
}

export default function AccountsTab({
  teamMembers,
  manualMode,
  setManualMode,
  selectedSenders,
  setSelectedSenders,
  manualSenders,
  setManualSenders,
  manualFirst,
  setManualFirst,
  manualLast,
  setManualLast,
  manualSenderError,
  setManualSenderError,
  showManualSenderForm,
  setShowManualSenderForm,
  selectedDomains,
  suggestions,
  accountGenerationMap,
  setAccountGenerationMap,
  emailAccountsPerSender,
  setEmailAccountsPerSender,
  signature,
  setSignature,
  canPreview,
  buildPreview,
  manualAccounts,
  setManualAccounts,
  duplicateEmailMap,
  preview,
  setPreview,
  showPreview,
  setShowPreview,
  currencyCode,
  googlePriceCents,
}: Props) {
  const [showFormAbovePreview, setShowFormAbovePreview] = useState(false)

  const addManualAccount = () => {
    const id = `${Date.now()}`
    setManualAccounts((prev) => [
      ...prev,
      {
        id,
        first: "",
        last: "",
        local: "",
        domain: "",
        signature: "",
        open: true,
        status: "draft",
      },
    ])
    setShowPreview(true)
    setShowFormAbovePreview(false)
  }

  // Reset to form view when all preview items are removed
  // But keep preview visible if there are manual accounts with status "ready"
  useEffect(() => {
    const hasReadyManualAccounts = manualAccounts.some(
      (a) => a.status === "ready",
    )
    if (preview.length === 0) {
      if (hasReadyManualAccounts) {
        // Keep preview visible if there are ready manual accounts
        setShowPreview(true)
      } else {
        // Hide preview if there are no ready manual accounts - ensure form is visible
        setShowPreview(false)
      }
    } else if (preview.length > 0) {
      // If preview has items, ensure preview is shown
      setShowPreview(true)
    }
  }, [preview.length, setShowPreview, manualAccounts])

  // Reset showFormAbovePreview when switching to manual mode
  useEffect(() => {
    if (manualMode) {
      setShowFormAbovePreview(false)
    }
  }, [manualMode])

  // Track previous manualMode to detect when user first enters manual mode
  const prevManualModeRef = useRef(manualMode)

  // In manual mode, ensure there's at least one draft account when first entering manual mode
  // But allow users to remove all accounts if they want to
  useEffect(() => {
    const justEnteredManualMode = manualMode && !prevManualModeRef.current
    prevManualModeRef.current = manualMode

    if (!manualMode) return
    if ((manualAccounts || []).length > 0) return

    // Only auto-add when first entering manual mode, not when user removes the last account
    if (!justEnteredManualMode) return

    const newId = `${Date.now()}`
    setManualAccounts(() => [
      {
        id: newId,
        first: "",
        last: "",
        local: "",
        domain: "",
        signature,
        open: true,
        status: "draft",
      },
    ])
  }, [manualMode, manualAccounts, setManualAccounts, signature])

  // Hide form when preview is first shown (when showPreview transitions from false to true)
  const prevShowPreviewRef = useRef(showPreview)
  const prevPreviewLengthRef = useRef(preview.length)

  useEffect(() => {
    if (showPreview && !prevShowPreviewRef.current && preview.length > 0) {
      setShowFormAbovePreview(false)
    }
    prevShowPreviewRef.current = showPreview
  }, [showPreview, preview.length])

  // Hide form after adding more accounts (when preview length increases and form is above preview)
  useEffect(() => {
    if (
      showFormAbovePreview &&
      preview.length > prevPreviewLengthRef.current &&
      preview.length > 0
    ) {
      setShowFormAbovePreview(false)
    }
    prevPreviewLengthRef.current = preview.length
  }, [preview.length, showFormAbovePreview])

  const accountGenerationDomains = useMemo(
    () =>
      suggestions
        .filter((s) => s.status === "ready" && accountGenerationMap[s.id])
        .map((s) => s.domain as string),
    [suggestions, accountGenerationMap],
  )

  const autoSignatureSample = useMemo(() => {
    if (manualMode) return null
    const norm = (value: string | undefined | null) => (value || "").trim()

    for (const member of teamMembers) {
      const key = `${member.id}`
      if (!selectedSenders[key]) continue
      const fullName = member.full_name || ""
      const parts = fullName.trim().split(/\s+/).filter(Boolean)
      const first = norm(parts[0] || member.first_name)
      const last = norm(
        parts.length > 1 ? parts.slice(1).join(" ") : member.last_name,
      )
      if (first || last) {
        return { firstName: first, lastName: last }
      }
    }

    for (const sender of manualSenders) {
      const key = `manual:${sender.id}`
      if (!selectedSenders[key]) continue
      const first = norm(sender.firstName)
      const last = norm(sender.lastName)
      if (first || last) {
        return { firstName: first, lastName: last }
      }
    }

    return null
  }, [manualMode, teamMembers, selectedSenders, manualSenders])

  const readyManualAccountCards = useMemo(() => {
    return manualAccounts
      .filter((acc) => acc.status === "ready")
      .filter(
        (acc) =>
          !preview.some(
            (r) =>
              r.status === "ready" &&
              (r as any).local === acc.local &&
              (r as any).domain === acc.domain,
          ),
      )
      .map((acc) => (
        <EmailAccountCard
          key={`manual-${acc.id}`}
          id={acc.id}
          mode="preview"
          defaultOpen={false}
          account={{
            first: acc.first,
            last: acc.last,
            local: acc.local,
            domain: acc.domain,
            signature: acc.signature,
          }}
          allowedDomains={selectedDomains}
          onChange={(updated) => {
            setManualAccounts((prev) =>
              prev.map((a) =>
                a.id === acc.id
                  ? {
                      ...a,
                      first: updated.first,
                      last: updated.last,
                      local: updated.local,
                      domain: updated.domain,
                      signature: updated.signature ?? "",
                    }
                  : a,
              ),
            )
          }}
          onRemove={() =>
            setManualAccounts((prev) => prev.filter((a) => a.id !== acc.id))
          }
        />
      ))
  }, [manualAccounts, preview, selectedDomains, setManualAccounts])

  return (
    <div className="email-accounts-provisioning">
      <h1 className="text-lg-medium mb-1">
        {i18n.t("js.email_accounts.accounts.title")}
      </h1>
      <p className="text-xs text-body-secondary mb-6">
        {i18n.t("js.email_accounts.accounts.subtitle")}
      </p>

      <div className="mt-4">
        <h2 className="text-md-medium mb-2">
          {i18n.t("js.email_accounts.accounts.senders")}
        </h2>
        {manualMode ? (
          <p className="text-xs text-body-secondary mb-6">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t(
                  "js.email_accounts.accounts.email_accounts_manual_creation_description_html",
                ),
              }}
            />
            <Button
              variant="linkGrey"
              size="sm"
              className="ms-1"
              onClick={() => setManualMode(!manualMode)}
            >
              {i18n.t("js.email_accounts.accounts.switch_to_auto_mode")}
            </Button>
          </p>
        ) : (
          <p className="text-xs text-body-secondary mb-6">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t(
                  "js.email_accounts.accounts.email_accounts_auto_creation_description_html",
                ),
              }}
            />
            <Button
              variant="linkGrey"
              size="sm"
              className="ms-1"
              onClick={() => setManualMode(!manualMode)}
            >
              {i18n.t("js.email_accounts.accounts.switch_to_manual_mode")}
            </Button>
          </p>
        )}
      </div>

      {!manualMode && (showFormAbovePreview || !showPreview) && (
        <div className="mt-4" data-form-section>
          {!manualMode && (
            <>
              <div className="domain-suggestions-list mb-6">
                {teamMembers.map((member, index) => {
                  const senderKey = `${member.id ?? member.email ?? `team-${index}`}`
                  return (
                    <div key={member.id} className="domain-suggestion">
                      <Checkbox
                        id={`sender-${member.id}`}
                        checked={!!selectedSenders[senderKey]}
                        onChange={(e) =>
                          setSelectedSenders((prev) => ({
                            ...prev,
                            [senderKey]: e.target.checked,
                          }))
                        }
                      />
                      <label
                        htmlFor={`sender-${member.id}`}
                        className="flex-grow-1 text-xs-medium mb-0"
                      >
                        {member.full_name ?? (member as any).name ?? ""}
                        <span className="text-2xs text-body-secondary">
                          {" "}
                          · {member.email}
                        </span>
                      </label>
                    </div>
                  )
                })}

                {manualSenders.map((sender) => {
                  const senderKey = `manual:${sender.id}`
                  return (
                    <div className="domain-suggestion" key={sender.id}>
                      <Checkbox
                        id={`sender-${sender.id}`}
                        checked={!!selectedSenders[senderKey]}
                        onChange={(e) =>
                          setSelectedSenders((prev) => ({
                            ...prev,
                            [senderKey]: e.target.checked,
                          }))
                        }
                      />
                      <Label
                        htmlFor={`sender-${sender.id}`}
                        className="flex-grow-1 text-xs-medium mb-0"
                      >
                        {sender.firstName} {sender.lastName}
                        <span className="text-2xs text-body-secondary">
                          {" "}
                          ·{" "}
                          {i18n.t("js.email_accounts.accounts.manually_added")}
                        </span>
                      </Label>
                      <Button
                        variant="default"
                        iconOnly
                        size="xs"
                        aria-label={i18n.t(
                          "js.email_accounts.accounts.remove_sender",
                        )}
                        onClick={() => {
                          setManualSenders((prev) =>
                            prev.filter((s) => s.id !== sender.id),
                          )
                          setSelectedSenders((prev) => {
                            const next = { ...prev }
                            delete next[senderKey]
                            return next
                          })
                        }}
                      >
                        <Icon icon="trash" aria-hidden="true" size="xs" />
                      </Button>
                    </div>
                  )
                })}

                {!showManualSenderForm ? (
                  <Button
                    variant="linkGrey"
                    size="sm"
                    onClick={() => setShowManualSenderForm(true)}
                    className="mt-2 ml-6"
                  >
                    {i18n.t("js.email_accounts.accounts.add_sender_manually")}
                  </Button>
                ) : (
                  <div className="domain-suggestion domain-suggestion--manual">
                    <Input
                      size="sm"
                      placeholder={i18n.t(
                        "js.email_accounts.accounts.first_name",
                      )}
                      value={manualFirst}
                      onChange={(e) => setManualFirst(e.target.value)}
                      fullWidth
                    />
                    <Input
                      size="sm"
                      placeholder={i18n.t(
                        "js.email_accounts.accounts.last_name",
                      )}
                      value={manualLast}
                      onChange={(e) => setManualLast(e.target.value)}
                      fullWidth
                    />
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => {
                        const fn = manualFirst.trim()
                        const ln = manualLast.trim()
                        if (!fn || !ln) return
                        const norm = (s: string) => s.trim().toLowerCase()
                        const full = `${norm(fn)} ${norm(ln)}`
                        const teamClash = teamMembers.some(
                          (m) =>
                            norm(m.full_name ?? (m as any).name ?? "") === full,
                        )
                        const manualClash = manualSenders.some(
                          (s) =>
                            `${norm(s.firstName)} ${norm(s.lastName)}` === full,
                        )
                        if (teamClash) {
                          setManualSenderError(
                            i18n.t(
                              "js.email_accounts.provisioning.manual_sender_duplicate_team",
                            ),
                          )
                          return
                        }
                        if (manualClash) {
                          setManualSenderError(
                            i18n.t(
                              "js.email_accounts.provisioning.manual_sender_duplicate",
                            ),
                          )
                          return
                        }
                        const id = `${Date.now()}`
                        setManualSenders((prev) => [
                          ...prev,
                          { id, firstName: fn, lastName: ln },
                        ])
                        setSelectedSenders((prev) => ({
                          ...prev,
                          [`manual:${id}`]: true,
                        }))
                        setManualFirst("")
                        setManualLast("")
                        setManualSenderError(undefined)
                      }}
                      disabled={!manualFirst.trim() || !manualLast.trim()}
                    >
                      {i18n.t("js.email_accounts.accounts.add_sender_manually")}
                    </Button>
                  </div>
                )}
                {manualSenderError && (
                  <Hint variant="error" className="mt-2">
                    {manualSenderError}
                  </Hint>
                )}
              </div>
            </>
          )}

          {!manualMode && (
            <div className="mb-6">
              <Label>{i18n.t("js.email_accounts.accounts.domains")}</Label>
              <p className="text-2xs text-hint mb-2">
                {i18n.t("js.email_accounts.accounts.domains_info")}
              </p>
              <DropdownMenu className="position-relative">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-select h-select--block"
                  >
                    {accountGenerationDomains.length > 0
                      ? accountGenerationDomains.join(" · ")
                      : i18n.t(
                          "js.email_accounts.accounts.no_domains_selected",
                        )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="dropdown-menu--full">
                  {suggestions
                    .filter(
                      (s) =>
                        s.status === "ready" &&
                        selectedDomains.includes(s.domain as string),
                    )
                    .map((s) => (
                      <DropdownMenuItem
                        key={s.id}
                        className="dropdown-item--clickable"
                      >
                        <Checkbox
                          id={`domain-${s.id}`}
                          checked={!!accountGenerationMap[s.id]}
                          onChange={(e) =>
                            setAccountGenerationMap((prev) => ({
                              ...prev,
                              [s.id]: e.target.checked,
                            }))
                          }
                        />
                        <Label htmlFor={`domain-${s.id}`} className="text-xs">
                          {s.domain}
                        </Label>
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {!manualMode && (
            <div className="mb-6">
              <Label>
                {i18n.t("js.email_accounts.accounts.email_accounts_per_sender")}
              </Label>
              <p className="text-2xs text-hint mb-2">
                {i18n.t(
                  "js.email_accounts.accounts.email_accounts_per_sender_info",
                )}
              </p>
              <NumberPicker
                value={emailAccountsPerSender}
                onChange={setEmailAccountsPerSender}
                min={1}
                ariaLabelPrefix={i18n.t(
                  "js.email_accounts.accounts.email_accounts_per_sender",
                )}
              />
            </div>
          )}

          {!manualMode && (
            <div className="mb-6">
              <Label>{i18n.t("js.email_accounts.accounts.signature")}</Label>
              <p className="text-2xs text-hint mb-2">
                {i18n.t("js.email_accounts.accounts.signature_hint")}
              </p>
              <SignatureEditor
                id="eap"
                value={signature}
                onChange={setSignature}
                previewSample={autoSignatureSample}
                selectedSenders={selectedSenders}
              />
            </div>
          )}
          {!manualMode && (
            <Button
              variant="default"
              fullWidth
              disabled={!canPreview}
              onClick={buildPreview}
            >
              {i18n.t("js.email_accounts.accounts.preview_email_accounts")}
            </Button>
          )}
        </div>
      )}

      {showPreview &&
        (preview.length > 0 ||
          manualAccounts.some((a) => a.status === "ready")) && (
          <div className="mt-4">
            {!manualMode && !showFormAbovePreview && (
              <div className="mb-3">
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => {
                    setShowFormAbovePreview((prev) => {
                      if (!prev) {
                        // Scroll to the form section after state update
                        setTimeout(() => {
                          const formSection = document.querySelector(
                            "[data-form-section]",
                          )
                          if (
                            formSection &&
                            typeof formSection.scrollIntoView === "function"
                          ) {
                            formSection.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            })
                          }
                        }, 100)
                      }
                      return true
                    })
                  }}
                  disabled={
                    preview?.some((row) => row.status === "loading") ?? false
                  }
                >
                  {preview?.some((row) => row.status === "loading")
                    ? i18n.t(
                        "js.email_accounts.accounts.preparing_email_accounts",
                      )
                    : i18n.t(
                        "js.email_accounts.accounts.add_more_email_accounts",
                      )}
                </Button>
              </div>
            )}
            <div>
              {preview.map((row) => {
                if (row.status === "loading") {
                  return (
                    <EmailAccountCard
                      key={row.id}
                      id={row.id}
                      summary={
                        <>
                          <Icon
                            icon="spinner-third"
                            className="fa-spin mr-2"
                            aria-hidden="true"
                          />
                          <span className="text-xs text-shimmer">
                            {i18n.t(
                              "js.email_accounts.provisioning.generating_email_account",
                            )}
                          </span>
                        </>
                      }
                      defaultOpen={false}
                    >
                      <div />
                    </EmailAccountCard>
                  )
                }
                const ready = row
                const readyId = ready.id // Capture ID in a const to ensure closure works correctly
                const emailKey = `${(ready.local || "").trim().toLowerCase()}@${(ready.domain || "").trim().toLowerCase()}`
                const isDuplicate = duplicateEmailMap[emailKey] > 1
                return (
                  <EmailAccountCard
                    key={readyId}
                    id={readyId}
                    mode="preview"
                    defaultOpen={false}
                    account={{
                      first: ready.first,
                      last: ready.last,
                      local: ready.local,
                      domain: ready.domain,
                      signature: ready.signature,
                    }}
                    allowedDomains={selectedDomains}
                    duplicate={isDuplicate}
                    onChange={(updated) => {
                      setPreview((prev) =>
                        prev.map((r) =>
                          r.id === readyId && r.status === "ready"
                            ? {
                                ...r,
                                first: updated.first,
                                last: updated.last,
                                local: updated.local,
                                domain: updated.domain,
                                signature: updated.signature ?? "",
                              }
                            : r,
                        ),
                      )
                    }}
                    onRemove={() => {
                      setPreview((prev) =>
                        prev.filter((r) => String(r.id) !== String(readyId)),
                      )
                    }}
                    currencyCode={currencyCode}
                    emailPriceCents={googlePriceCents}
                  />
                )
              })}

              {readyManualAccountCards}
            </div>
          </div>
        )}

      {manualMode && (
        <div className="mt-4" data-form-section>
          <ManualAccountsSection
            accounts={manualAccounts}
            domains={selectedDomains}
            duplicateEmailMap={duplicateEmailMap}
            onChange={(next) => setManualAccounts(() => next)}
            signatureForNew={signature}
            className="mb-4"
            showIncomplete={manualMode}
            currencyCode={currencyCode}
            emailPriceCents={googlePriceCents}
          />
          <div className="mt-4">
            <Button
              variant="default"
              fullWidth
              onClick={addManualAccount}
              disabled={manualAccounts.some(
                (acc) =>
                  acc.status !== "ready" &&
                  !(acc.first || acc.last || acc.local || acc.domain),
              )}
            >
              {i18n.t("js.email_accounts.accounts.add_more_email_accounts")}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
